import { useCallback, useMemo, useState } from "react";
/**
 * isDefault: true인 리프 노드의 경로(인덱스 배열)를 찾는 함수
 */ const findDefaultPath = function(items) {
    let path = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [];
    for(let i = 0; i < items.length; i++){
        const item = items[i];
        const currentPath = [
            ...path,
            i
        ];
        // isDefault가 true인 리프 노드 발견
        if (item.isDefault && item.sourceId) {
            return currentPath;
        }
        // children이 있으면 재귀 탐색
        if (item.children && item.children.length > 0) {
            const result = findDefaultPath(item.children, currentPath);
            if (result) return result;
        }
    }
    return null;
};
/**
 * IP Select 필터 상태 관리 훅
 * 재귀적 트리 구조에서 각 depth별 선택 상태를 관리
 */ export const useIpSelect = (param)=>{
    let { items , depthLabels , onSelect  } = param;
    // isDefault: true인 리프 노드의 경로를 초기값으로 사용
    const defaultIndices = useMemo(()=>findDefaultPath(items) || [], [
        items
    ]);
    const [selectedIndices, setSelectedIndices] = useState(defaultIndices);
    const [openDepth, setOpenDepth] = useState(null);
    // 각 depth별 선택 가능한 옵션 계산
    const optionsByDepth = useMemo(()=>{
        const result = [
            items
        ];
        for(let i = 0; i < selectedIndices.length; i++){
            const currentOptions = result[i];
            const selectedIndex = selectedIndices[i];
            const selectedItem = currentOptions === null || currentOptions === void 0 ? void 0 : currentOptions[selectedIndex];
            if (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.children) {
                result.push(selectedItem.children);
            } else {
                break;
            }
        }
        return result;
    }, [
        items,
        selectedIndices
    ]);
    // 각 depth별 선택된 아이템 (간단한 map 연산이므로 useMemo 불필요)
    const selectedItems = selectedIndices.map((index, depth)=>{
        var _optionsByDepth_depth;
        return (_optionsByDepth_depth = optionsByDepth[depth]) === null || _optionsByDepth_depth === void 0 ? void 0 : _optionsByDepth_depth[index];
    });
    // 선택된 leaf 노드 정보 (간단한 연산이므로 useMemo 불필요)
    const lastSelected = selectedItems[selectedItems.length - 1];
    const selectedSource = (lastSelected === null || lastSelected === void 0 ? void 0 : lastSelected.sourceId) && (lastSelected === null || lastSelected === void 0 ? void 0 : lastSelected.sourceType) ? {
        sourceId: lastSelected.sourceId,
        sourceType: lastSelected.sourceType,
        label: lastSelected.label
    } : null;
    // 선택 완료 여부
    const isComplete = selectedSource !== null;
    // depth별 선택 핸들러
    const handleSelect = useCallback((depth, index)=>{
        // 선택된 아이템 확인을 위해 items와 인덱스를 직접 계산
        const calculateOptionsByDepth = (indices)=>{
            const result = [
                items
            ];
            for(let i = 0; i < indices.length; i++){
                const currentOptions = result[i];
                const selectedIdx = indices[i];
                const selectedItem = currentOptions === null || currentOptions === void 0 ? void 0 : currentOptions[selectedIdx];
                if (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.children) {
                    result.push(selectedItem.children);
                } else {
                    break;
                }
            }
            return result;
        };
        setSelectedIndices((prev)=>{
            var _calculatedOptions_depth;
            // 해당 depth까지의 선택만 유지하고 새로 선택
            const newIndices = prev.slice(0, depth);
            newIndices[depth] = index;
            // 새로운 인덱스로 optionsByDepth 계산
            const calculatedOptions = calculateOptionsByDepth(newIndices);
            const selectedItem = (_calculatedOptions_depth = calculatedOptions[depth]) === null || _calculatedOptions_depth === void 0 ? void 0 : _calculatedOptions_depth[index];
            // 선택된 아이템에 따라 처리
            if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.sourceId) && (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.sourceType)) {
                // leaf 노드 선택 시 콜백 호출
                onSelect === null || onSelect === void 0 ? void 0 : onSelect({
                    sourceId: selectedItem.sourceId,
                    sourceType: selectedItem.sourceType
                });
                setOpenDepth(null);
            } else if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.children) && selectedItem.children.length > 0) {
                // children이 있으면 다음 depth 열기
                setOpenDepth(depth + 1);
            }
            return newIndices;
        });
    }, [
        items,
        onSelect
    ]);
    // 드롭다운 열기/닫기 (의존성이 없고 간단한 함수이므로 useCallback 불필요)
    const handleToggleDropdown = (depth)=>{
        setOpenDepth((prev)=>prev === depth ? null : depth);
    };
    // 드롭다운 닫기 (의존성이 없고 간단한 함수이므로 useCallback 불필요)
    const handleCloseDropdown = ()=>{
        setOpenDepth(null);
    };
    // 현재 보여줄 필터 개수 (선택된 depth + 1)
    const visibleDepthCount = Math.min(selectedIndices.length + 1, depthLabels.length);
    return {
        // 상태
        selectedIndices,
        selectedItems,
        selectedSource,
        isComplete,
        optionsByDepth,
        openDepth,
        visibleDepthCount,
        // 액션
        handleSelect,
        handleToggleDropdown,
        handleCloseDropdown
    };
};
